import React from 'react';
import { withTheme } from 'styled-components';
import FlightSearchForm from '@g4/next-furniture/FlightSearchForm';
import moment from '@g4/moment-utc';
import PropTypes from 'prop-types';

import { useSSRQuery, useLazySSRQuery } from '../../../../graphql';
import useFlightSearchCriteria from '../../../../hooks/useFlightSearchCriteria';

import INITIAL_DATA_QUERY from './wrappedFlightSearchForm/initialData.query.graphql.js';
import NEARBY_FLIGHT_LOCATIONS_QUERY from './wrappedFlightSearchForm/nearbyFlightLocations.query.graphql.js';
import FLIGHT_MARKET_QUERY from './wrappedFlightSearchForm/flightMarket.query.graphql.js';

const getDateValue = date => {
  if (!date) {
    return undefined;
  }

  return moment(date);
};

const searchCriteriaToProps = searchCriteria => {
  if (!searchCriteria) {
    return undefined;
  }

  // HACK; the childrenCount contains the number of seated infants
  const childrenCount = searchCriteria?.childrenCount ?? 0;

  return {
    ...searchCriteria,
    departDate: getDateValue(searchCriteria?.departDate?.date),
    returnDate: getDateValue(searchCriteria?.returnDate?.date),
    childrenCount,
    // HACK; lapInfantDobs.length on flight search criteria is the number of seated infants
    seatInfantCount: searchCriteria?.lapInfantDobs?.length,
  };
};

const WrappedFlightSearchForm = ({ children, ...props }) => {
  const flightSearchCriteriaResponse = useFlightSearchCriteria();

  const initialDataResponse = useSSRQuery(INITIAL_DATA_QUERY, { variables: { isBooking: true } });

  const [fetchNearbyLocations, nearbyLocationsResponse] = useLazySSRQuery(
    NEARBY_FLIGHT_LOCATIONS_QUERY
  );

  const [fetchMarketQuery, marketResponse] = useLazySSRQuery(FLIGHT_MARKET_QUERY);

  const fetchMarket = React.useCallback(
    ({ variables, ...rest } = {}) => {
      const { operatedBy, ...otherVariables } = variables || {};
      return fetchMarketQuery({
        variables: {
          ...otherVariables,
          operatedBy: operatedBy != null
            ? (Array.isArray(operatedBy) ? operatedBy : [operatedBy])
            : operatedBy,
        },
        ...rest,
      });
    },
    [fetchMarketQuery]
  );


  if (initialDataResponse.loading) {
    return null;
  }

  const errors = [
    initialDataResponse.error,
    marketResponse.error,
    nearbyLocationsResponse.error,
  ].filter(Boolean);

  if (errors.length) {
    // Give our custom error a human-readable message
    const error = new Error(
      `Failed to query flight search form data: ${errors.join(', ')}`
    );

    // Add each GraphQL error to the new custom error
    error.apiErrors = errors;

    throw error;
  }

  const translations =
    initialDataResponse.data.application.translations.flightSearchForm;
  const configurations = initialDataResponse.data.application.configurations;
  const locations = initialDataResponse.data.flightLocations;
  const sunCountryImage = initialDataResponse?.data?.application?.images?.find(
      (image) => image.id === 'sunCountryDropdownImage'
    );

  const renderForm = formProps => (
    <FlightSearchForm
      translations={translations}
      dropDownLogo={sunCountryImage}
      configurations={configurations}
      initialValues={searchCriteriaToProps(flightSearchCriteriaResponse?.data)}
      locations={locations}
      fetchNearbyLocations={fetchNearbyLocations}
      nearbyLocationsLoading={nearbyLocationsResponse.loading}
      nearbyLocations={
        nearbyLocationsResponse.data
          ? nearbyLocationsResponse.data.flightLocations
          : []
      }
      fetchMarket={fetchMarket}
      marketLoading={marketResponse.loading}
      market={marketResponse.data}
      isTrackingEnabled={false}
      {...props}
      {...formProps}
    />
  );

  if (typeof children === 'function') {
    return children(renderForm);
  }

  return renderForm(props);
};

WrappedFlightSearchForm.propTypes = {
  children: PropTypes.node,
};

export default withTheme(WrappedFlightSearchForm);
